import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemText, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import PageWrapper from '../components/PageWrapper';
import SectionDivider from '../components/SectionDivider';

const About = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const renderFacultySectionForMobile = (config) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {config.faculty.map((faculty, index) => (
                <Card key={index} sx={{
                    backgroundColor: index % 2 === 1 ? 'background.paper' : 'action.hover',
                    '&:hover': {
                        boxShadow: 6,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease-in-out'
                    },
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: 2
                }}>
                    <CardContent>
                        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {faculty.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="primary">Subject</Typography>
                                <Typography variant="body2">{faculty.subject}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="primary">Grade Level</Typography>
                                <Typography variant="body2">{faculty.gradeLevel}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="primary">Experience</Typography>
                                <Typography variant="body2">{faculty.experience}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="primary">License</Typography>
                                <Typography variant="body2">{faculty.license}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="primary">Education</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{faculty.education}</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );

    const renderFacultySectionForDesktop = (config) => (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            mb: 4
        }}>
            {config.faculty.map((faculty, index) => {
                const row = Math.floor(index / 2);
                const col = index % 2;
                const isOdd = (row + col) % 2 === 1;

                return (
                    <Card key={index} sx={{
                        backgroundColor: isOdd ? 'background.paper' : 'action.hover',
                        '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-2px)',
                            transition: 'all 0.3s ease-in-out'
                        },
                        transition: 'all 0.3s ease-in-out',
                        boxShadow: 2
                    }}>
                        <CardContent>
                            <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {faculty.name}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box>
                                    <Typography variant="subtitle2" color="primary">Subject</Typography>
                                    <Typography variant="body2">{faculty.subject}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="primary">Grade Level</Typography>
                                    <Typography variant="body2">{faculty.gradeLevel}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="primary">Experience</Typography>
                                    <Typography variant="body2">{faculty.experience}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="primary">License</Typography>
                                    <Typography variant="body2">{faculty.license}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="primary">Education</Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{faculty.education}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                );
            })}
        </Box>
    );

    return (
        <PageWrapper configKey="about" pageTitle="About MacGillis Global Academy">
            {(config) => (
                <Container maxWidth="md" sx={{ mt: 3, mb: 3 }}>
                    {/* Head of School Welcome Section */}
                    <Box sx={{ mb: 6 }}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ mb: 4, textAlign: { xs: 'left', sm: 'center' } }}>
                            Head of School Welcome
                        </Typography>
                        <Typography component="p" sx={{ mb: 2 }}>
                            Welcome to MacGillis Global Academy (MGA)!
                        </Typography>
                        <Typography component="p" sx={{ mb: 2 }}>
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/assets/images/head-of-school-photo.jpg`}
                                sx={{
                                    float: 'left',
                                    width: { xs: '120px', sm: '150px', md: '180px' },
                                    height: 'auto',
                                    mr: { xs: 2, sm: 3 },
                                    mb: { xs: 1, sm: 2 },
                                    borderRadius: 2,
                                    boxShadow: 2
                                }}
                                alt="Head of School - Lori Dembo"
                            />
                            MGA is a fully interactive, online global academy serving students in Grades 4–12. We are designed for learners seeking personalized academic programs in a warm, welcoming, and secure homeschool environment. Our talented, highly experienced faculty and our supportive community help students thrive both academically and socially.
                        </Typography>
                        <Typography component="p" sx={{ mb: 2 }}>
                            Originally founded in 2016 as MacGillis Education Group, an international online program certified by COGNIA, we have since expanded to serve students in both international and domestic programs. Soon to be homeschool-certified in the state of Maryland, we proudly support students across the United States and around the world. In close partnership with families, our mission is to help students achieve—and often exceed—their personal and academic goals as confident, independent learners.
                        </Typography>
                        <Typography component="p" sx={{ mb: 1 }}>
                            Our school values—wisdom, respect, and individualized, rigorous teaching—guide everything we do. We aim to cultivate independent-minded individuals who are intellectually curious, compassionate, and prepared for a purposeful life.
                        </Typography>
                        <List sx={{ listStyleType: 'disc', pl: 4, mb: 2, clear: 'left' }}>
                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                <ListItemText primary="Pursuit of Wisdom: We believe in the intrinsic value of learning and foster a lifelong love of knowledge." />
                            </ListItem>
                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                <ListItemText primary="Respect: We nurture a culture of respect for oneself, others, and the world, promoting inclusivity, empathy, and environmental responsibility." />
                            </ListItem>
                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                <ListItemText primary="Individualized & Rigorous Teaching: With small class sizes and personalized instruction, we provide a dynamic, challenging academic experience tailored to each learner." />
                            </ListItem>
                        </List>
                        <Typography component="p" sx={{ mb: 2 }}>
                            We take pride in our individualized approach, with many MGA students advancing into higher-level courses and excelling in their academic pursuits.
                        </Typography>
                        <Typography component="p" sx={{ mb: 2 }}>
                            To learn more about MacGillis Global Academy, we invite you to explore our website, attend an Open House, or join a bi-weekly Parent Forum to discover how MGA stands apart from other online programs.
                        </Typography>
                        <Typography component="p" sx={{ mb: 2 }}>
                            With 40 plus years in the field of academia I highly recommend MacGillis Global Academy!
                        </Typography>
                        <Box sx={{ mt: 3, mb: 2 }}>
                            <Typography component="p" sx={{ mb: 1 }}>
                                Sincerely,
                            </Typography>
                            <Box
                                component="img"
                                src={`${process.env.PUBLIC_URL}/assets/images/head-of-school-signature.jpg`}
                                sx={{
                                    maxWidth: '200px',
                                    height: 'auto',
                                    mb: 1
                                }}
                                alt="Head of School Signature"
                            />
                            <Typography component="p" sx={{ fontWeight: 'bold' }}>
                                Lori Dembo
                            </Typography>
                            <Typography component="p">
                                Head of School &<br/>
                                Executive Director, MGA
                            </Typography>
                        </Box>
                    </Box>

                    <SectionDivider />

                    {/* Our Vision Section */}
                    <Box sx={{ mt: 3, mb: 6 }}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                            Our Vision
                        </Typography>
                        <Typography component="p" sx={{ mb: 1 }}>
                            MacGillis Global Academy (MGA) aims to be a premier educational institution that combines the rigor of private instruction with the accessibility of remote learning.
                        </Typography>
                        <List sx={{ listStyleType: 'disc', pl: 4 }}>
                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                <ListItemText primary="We provide exceptional synchronous instruction delivered by experienced and dedicated teachers, fostering real-time interaction and personalized guidance." />
                            </ListItem>
                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                <ListItemText primary="We value independence and individuality, offering tailored learning opportunities that cultivate intellectual, social and emotional growth which is crucial for a child's future success." />
                            </ListItem>
                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                <ListItemText primary="Our educational community encourages exploration, love of learning, and the pursuit of academic passions." />
                            </ListItem>
                            <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                                <ListItemText primary="We strive to develop informed, open-minded, and compassionate individuals who positively contribute to local and global communities." />
                            </ListItem>
                        </List>
                        <Typography component="p" sx={{ mt: 1, mb: 2 }}>
                            MGA aims to redefine virtual learning, empowering students to reach their full potential in a remote setting.
                        </Typography>
                    </Box>

                    <SectionDivider />

                    {/* Our Core Values Section */}
                    <Box sx={{ mt: 3, mb: 6 }}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                            Our Core Values
                        </Typography>
                        <Typography component="p" sx={{ mb: 2 }}>
                            Our school values wisdom, respect, and an individualized and rigorous teaching and learning experience, aiming to cultivate independent-minded individuals who are intellectually curious, compassionate, and prepared for a meaningful life.
                        </Typography>
                        <Box sx={{ mb: 2, textAlign: 'left', width: { xs: '95%', sm: '75%' }, ml: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/assets/images/wisdom.png`}
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        opacity: 1.0
                                    }}
                                    alt="Wisdom Icon"
                                />
                                <Typography>
                                    <strong>Pursuit of Wisdom</strong>: We believe in the intrinsic value of acquiring knowledge and encourage a lifelong love for learning.
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{ mb: 2, textAlign: 'right', width: { xs: '95%', sm: '75%' }, mr: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography>
                                    <strong>Respect</strong>: We foster a culture of respect for oneself, others, and the world, promoting inclusivity, empathy, and environmental stewardship.
                                </Typography>
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/assets/images/respect.png`}
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        opacity: 1.0
                                    }}
                                    alt="Respect Icon"
                                />
                            </Box>
                        </Box>
                        <Box sx={{ mb: 2, textAlign: 'left', width: { xs: '95%', sm: '75%' }, ml: 'auto' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    component="img"
                                    src={`${process.env.PUBLIC_URL}/assets/images/teaching.png`}
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        opacity: 1.0
                                    }}
                                    alt="Teaching Icon"
                                />
                                <Typography>
                                    <strong>Individualized & Rigorous Teaching</strong>: We prioritize small class sizes and personalized attention, providing a stimulating and challenging learning environment.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <SectionDivider />

                    {/* Faculty and Staff Section */}
                    <Box sx={{ mt: 3, mb: 0 }}>
                        <Typography variant="h3" component="h2" gutterBottom sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                            Faculty and Staff
                        </Typography>
                        <Typography component="p" sx={{ mb: 2 }}>
                            We have a team of caring, motivated and experienced teachers. All of our teachers have been selected to work with us because they share our educational philosophy and have an impressive record of success in teaching. All have a teaching degree in relevant coursework, and all have extensive classroom experience. What makes our teachers unique is their passion for teaching, dedication to customizing instruction to individual students, engaging students in learning, and providing meaningful feedback that enables students to grow intellectually.
                        </Typography>
                        {isMobile ? renderFacultySectionForMobile(config) : renderFacultySectionForDesktop(config)}
                    </Box>
                </Container>
            )}
        </PageWrapper>
    );
};

export default About; 